import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ArrowRight, Phone, Mail, Megaphone, MessageCircle, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import socialMediaHeroBg from "@assets/image_1773678456390.webp";
import savoyLogo from "@assets/savoy-logo-2-150x150_1776849762460.webp";
import hhVanHireLogo from "@assets/hh-1_1776849762462.webp";

export default function SocialMediaManagement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      title: "Social media strategy",
      description: "Our team takes the time to understand your objectives and factor in your unique requirements to create a tailored and results-driven social strategy that will be focused on your business goals, helping you to maximise ROI on your campaigns."
    },
    {
      title: "Content marketing",
      description: "Social media is not just about creating a social presence; it requires a thorough understanding of your audience. We create content that gets people talking! From planning and account management to content sharing, our engaging text will be paired with specially curated images."
    },
    {
      title: "Paid social campaigns & ad management",
      description: "Social media platforms are great to increase organic reach, but it is essential to explore social media advertising as part of your strategy to derive true value. Paid social campaigns and ads enable you to take full advantage of your social media presence and amplify your reach. From creating audience profiles, considering campaign strategies, creating and sharing content, and monitoring campaigns; we have all the expertise required to achieve the best results."
    },
    {
      title: "Social media monitoring & reporting",
      description: "Demonstrating your social media campaigns and activities' ROI can be difficult, but with efficient monitoring and analysis, it is easy to keep track of your goals. Our detailed reports and analysis mean you can follow your activity and make informed decisions on your social media strategies."
    }
  ];

  const benefits = [
    {
      title: "Drive traffic & increase your reach",
      description: "Social media marketing offers the perfect opportunity for brands to reach diverse audiences. With the right social media strategy you can grow your business's reach and engagement, helping you generate new leads, increase sales and boost your online presence."
    },
    {
      title: "Build credibility",
      description: "Social media is not just about growing your brand, it is also about creating the right impression among your followers. Maximise your customer base and win their trust using a specialised social media marketing approach. Create brand awareness and build credibility using social media to your advantage!"
    },
    {
      title: "Stay ahead of the curve",
      description: "Digital marketing is getting increasingly competitive, with every brand now leveraging social media for their business needs. The Cornwall SEO Co can manage all your social media accounts so you can focus on your business – we can work on building a buzz around your brand."
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>Social Media Agency in Cornwall | The Cornwall SEO Co.</title>
        <meta name="description" content="Users interact with what they are interested in. Having higher interaction means that readers love your content, which can lead them to love your brand." />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 bg-black bg-cover bg-center"
        style={{ backgroundImage: `url(${socialMediaHeroBg})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-0"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter">
              Social Media Agency <br/>
              in <span className="text-primary">Cornwall</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              Increase your reach and connect with your audience! Over 3.8 billion users actively use social media. Let us help you leverage the power of social to grow your business.
            </p>
            <a href="#contact-form" className="inline-block">
              <Button size="lg" className="bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all cursor-pointer">
                Contact Us <ArrowRight className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-primary/20">
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">100% CORNWALL-BASED TEAM</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">NO MINIMUM TERM CONTRACT</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Social Media <br/> <span className="text-primary">Management</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed">
              If you are looking to increase your online presence, the Cornwall SEO Co. is here to help. We have worked with everyone from SMEs to large corporate brands in Cornwall and across the UK. Our social media services provide end-to-end management, audits, and reports to help you set up and optimise your social media presence. Twitter, LinkedIn, Facebook, Instagram, or any other platform; our social media experts are ready to help you engage with your audience, and subsequently grow your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-black p-6">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="font-heading font-black text-xl text-black uppercase italic">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-sm">
                      {feature.description}
                    </p>
                    {feature.title === "Paid social campaigns & ad management" && (
                      <Link href="/facebook-advertising.html" className="inline-block mt-6">
                          <Button className="bg-primary text-black hover:bg-black hover:text-white rounded-none font-black uppercase italic cursor-pointer">
                            Facebook Ads
                          </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-8 uppercase italic tracking-tighter">
              Why Choose <br/> <span className="text-primary">Social Media</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-24">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="space-y-4"
              >
                <Megaphone className="text-primary w-12 h-12" />
                <h3 className="text-2xl md:text-2xl font-heading font-black text-black uppercase italic">{benefit.title}</h3>
                <p className="text-black font-medium leading-relaxed text-lg md:text-base">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Client <span className="text-primary">Testimonials</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-black text-white border-2 border-primary p-8 md:p-10"
            >
              <div className="flex items-center justify-between gap-8 mb-8">
                <img src={savoyLogo} alt="Savoy Resort & Spa logo" className="h-20 md:h-24 w-auto max-w-[220px] object-contain bg-white p-3" />
                <Quote className="text-primary shrink-0" size={34} />
              </div>
              <p className="text-white/85 text-base md:text-lg leading-relaxed italic mb-6">The Savoy Resort & Spa would like to thank Page1 for the services provided. We realised the importance of social media and needed expertise in this field to help support and drive our primary online campaigns. Incredibly proactive following our briefs, Page1 has help the Savoy gain traction and exposure in a very competitive market. I would recommend any company needing fresh ideas to not hesitate making contact with Page1.</p>
              <p className="text-primary font-black uppercase italic tracking-widest text-sm">Savoy Resort & Spa</p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-black text-white border-2 border-primary p-8 md:p-10"
            >
              <div className="flex items-center justify-between gap-8 mb-8">
                <img src={hhVanHireLogo} alt="H&H Van Hire logo" className="h-20 md:h-24 w-auto max-w-[220px] object-contain bg-white p-3" />
                <Quote className="text-primary shrink-0" size={34} />
              </div>
              <p className="text-white/85 text-base md:text-lg leading-relaxed italic mb-6">I would like to take this opportunity to thank you for all your help and assistance with our social media websites, I created a Facebook page years ago but never had the time or the resources to do anything with it, since you have taken over we get a lot of interest, likes and comments etc. It looks very professional! Great addition to our website! Thank you!</p>
              <p className="text-primary font-black uppercase italic tracking-widest text-sm">H&H Van Hire</p>
            </motion.article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-form" className="py-12 md:py-24 bg-primary">
        <div className="container mx-auto px-3 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)] overflow-x-hidden">
            <div className="grid md:grid-cols-2 gap-4 md:gap-16">
              <div>
                <h3 className="text-3xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Ready to <br/> Build Your <br/> Brand?</h3>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl break-words">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl hover:text-primary transition-colors break-words">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
